import { ReactNode, useState } from 'react';

import { withRoleAccess } from '@app/routes';
import { useUserRole } from '@entities/user';
import { useUpdateWalletMutation, walletParserModes, walletStatuses } from '@entities/wallets';
import type {
  IWallet,
  IWalletTableColumnsDeposit,
  IWalletTableColumnsWithdrawal,
  TWalletType,
} from '@entities/wallets';
import CopyToClipboardButton from '@features/copy-to-clipboard-button';
import { ModalEditEntity } from '@features/modal-entity-edit';
import { ModalViewEntity } from '@features/modal-entity-view';
import ModalOpenBtn from '@features/modal-open-btn';
import ReduceAndCopyString from '@features/reduce-and-copy-string';
import { RefetchAuto } from '@features/refetch-auto';
import { ITableActionsConfig, TableActionBtns } from '@features/table-action-btns';
import { TableControlMenu } from '@features/table-control-menu';
import { FilterInput, FilterSelect, FilterControlBlock } from '@features/table-filter';
import { WalletCredentials } from '@features/wallets';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MenuItem, IconButton, Tooltip } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { TBody } from '@shared/components/Table';
import { getTypedObjectKeys } from '@shared/types/typedObjectEntries';

import { CloneWallet } from '../components/CloneWallet';
import { EditWalletSettings } from '../components/EditWalletSettings';
import { NewWallet } from '../components/NewWallet';
import { NewWalletMID } from '../components/NewWalletMID';
import { ParserModeToggler } from '../components/ParserModeToggler';
import { StatusToggler } from '../components/StatusToggler';
import { StopTrafficBtn } from '../components/StopTrafficBtn';

interface IProps {
  type: TWalletType;
  refetch?: () => void;
  setRefreshInterval?: (interval: string) => void;
  refreshInterval?: string;
  /** When false, child wallets are not rendered/expanded */
  showChildren?: boolean;
}

export const useTableSettings = ({
  type,
  refetch,
  refreshInterval,
  setRefreshInterval,
  showChildren = true,
}: IProps) => {
  const [isExpandAll, setIsExpandAll] = useState(true);
  const userRole = useUserRole();

  // THead settings
  const columnTitlesDeposit: IWalletTableColumnsDeposit = {
    name: 'Name',
    wallet_address: 'Wallet address',
    status_toggle: 'Status toggle',
    status: 'Status',
    stop_traffic: 'Stop traffic',
    mid: 'MID',
    actions: 'Actions',
  };

  const columnTitlesWithdrawal: IWalletTableColumnsWithdrawal = {
    name: 'Name',
    wallet_address: 'Wallet address',
    status_toggle: 'Status toggle',
    status: 'Status',
    parser_toggle: 'Parser toggle',
    parser_mode: 'Parser mode',
    mid: 'MID',
    actions: 'Actions',
  };

  if (userRole === 'operator') {
    delete columnTitlesDeposit.stop_traffic;
  }

  const columnTitles = type === 'deposit' ? columnTitlesDeposit : columnTitlesWithdrawal;

  const renderFilterElement = (
    key: keyof IWalletTableColumnsDeposit | keyof IWalletTableColumnsWithdrawal,
  ) => {
    switch (key) {
      case 'name': {
        if (!showChildren) {
          return <FilterInput name={key} />;
        }
        return (
          <div className='wallet-name__filter'>
            <Tooltip title='Expand/Collapse ALL' placement='right'>
              <IconButton onClick={() => setIsExpandAll(!isExpandAll)} aria-label='copy'>
                <ExpandMoreIcon />
              </IconButton>
            </Tooltip>

            <FilterInput name={key} />
          </div>
        );
      }
      case 'status_toggle':
      case 'parser_toggle':
      case 'stop_traffic':
        return null;

      case 'status':
        return (
          <FilterSelect name={key} options={walletStatuses} filterKey={`filter[statuses][]`} />
        );

      case 'parser_mode':
        return type === 'withdraw' ? <FilterSelect name={key} options={walletParserModes} /> : null;

      case 'actions':
        const NewWalletBtn = withRoleAccess(ModalOpenBtn, ['admin', 'operator']);

        return (
          <FilterControlBlock showVisibilityBtn={false}>
            <TableControlMenu>
              <MenuItem>
                <NewWalletBtn
                  text='New wallet'
                  icon={<AccountBalanceWalletIcon color='success' />}
                  modalContent={<NewWallet type={type} refetch={refetch} />}
                />
              </MenuItem>
              <MenuItem data-menu-type='dropdown'>
                <RefetchAuto
                  refreshInterval={refreshInterval}
                  onChangeInterval={setRefreshInterval}
                />
              </MenuItem>
            </TableControlMenu>
          </FilterControlBlock>
        );

      default:
        return <FilterInput name={key} />;
    }
  };

  // TBody settings

  const renderRow = (wallet: IWallet, defaultRow: ReactNode) => {
    const isParentWallet = wallet.parent_id === null;
    const hasChildWallets = showChildren && wallet.childs.length > 0;

    const customRow = () => {
      return (
        <tr key={wallet.id} className={`${isParentWallet ? 'parent-wallet' : 'child-wallet'}`}>
          <td colSpan={Object.keys(columnTitles).length}>
            <Accordion className='accordion-block' defaultExpanded={isExpandAll}>
              <table>
                <tbody>
                  <tr>
                    {getTypedObjectKeys(columnTitles).map(key => {
                      return (
                        <td key={key}>
                          <div className='table-column__name'>{columnTitles[key]}:</div>
                          {renderItem(key, wallet)}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
              <AccordionDetails className='accordion-details'>
                <table>
                  <TBody items={wallet.childs} renderItem={renderItem} />
                </table>
              </AccordionDetails>
            </Accordion>
          </td>
        </tr>
      );
    };

    return hasChildWallets ? customRow() : defaultRow;
  };

  const renderItem = (
    key: keyof IWalletTableColumnsDeposit | keyof IWalletTableColumnsWithdrawal,
    wallet: IWallet,
  ) => {
    const isParentWallet = wallet.parent_id === null;

    switch (key) {
      case 'name': {
        if (isParentWallet) {
          const liveChilds = wallet.childs.filter(child => child.status === 'live').length;

          if (!showChildren) {
            return (
              <div className='wallet-name-block'>
                <div className='wallet-name'>
                  <div className='table-cell-items' style={{ overflow: 'hidden' }}>
                    <span title={wallet.name}>{wallet.name}</span>
                    <CopyToClipboardButton text={wallet.name} />
                  </div>
                </div>
                {(liveChilds >= 1 || wallet.status === 'live') && (
                  <span className='wallet-status__indicator'>
                    {liveChilds >= 1 ? liveChilds : ''}
                  </span>
                )}
              </div>
            );
          }

          return (
            <div className='wallet-name-block'>
              <div className='wallet-name'>
                <div className='table-cell-items' style={{ overflow: 'hidden' }}>
                  <AccordionSummary
                    className='accordion-summary'
                    expandIcon={<ExpandMoreIcon />}
                    key={key}
                  >
                    <span title={wallet.name}>{wallet.name}</span>
                  </AccordionSummary>
                  <CopyToClipboardButton text={wallet.name} />
                </div>
              </div>
              {(liveChilds >= 1 || wallet.status === 'live') && (
                <span className='wallet-status__indicator'>
                  {liveChilds >= 1 ? liveChilds : ''}
                </span>
              )}
            </div>
          );
        }

        return <span title={wallet.name}>{wallet.name}</span>;
      }

      case 'wallet_address':
        return <ReduceAndCopyString text={wallet[key]} needReduce={false} />;

      case 'status_toggle':
        return <StatusToggler wallet={wallet} refetch={refetch} />;

      case 'parser_toggle':
        return type === 'withdraw' ? (
          <ParserModeToggler id={wallet.id} parserMode={wallet.parser_mode} refetch={refetch} />
        ) : null;

      case 'stop_traffic': {
        const isParentWallet = wallet.parent_id === null;
        const StopTraffic = withRoleAccess(StopTrafficBtn, ['admin', 'operator', 'provider']);
        return isParentWallet && type === 'deposit' ? (
          <StopTraffic wallet={wallet} refetch={refetch} />
        ) : null;
      }

      case 'parser_mode':
        return type === 'withdraw' ? (
          <span title={wallet.parser_mode} className={`wallet-parser-mode-${wallet.parser_mode}`}>
            {wallet.parser_mode}
          </span>
        ) : null;

      case 'mid':
        return <span title={wallet.mid.name}>{wallet.mid.name}</span>;

      case 'status':
        return (
          <span title={wallet.status} className={`wallet-status-${wallet.status}`}>
            {wallet.status}
          </span>
        );

      case 'actions': {
        const excludeKeysEdit: (keyof IWallet)[] = [
          'id',
          'parent_id',
          'merchant_id',
          'merchant_short_name',
          'currency_id',
          'parser_mode',
          'type',
          'status',
          'wallet_group_id',
          'qr_code_static',
          'reason',
          'wallet_policy',
          'limit_preset',
          'merchant_id',

          // Objects
          'bank',
          'mid',
          'childs',
          'settings',
        ];

        const excludeKeysView: (keyof IWallet)[] = [
          'merchant_id',
          'currency_id',
          'wallet_group_id',
          'qr_code_static',
          'reason',
          'wallet_policy',
          'limit_preset',
          'settings',
        ];

        if (type === 'deposit') {
          excludeKeysView.push('parser_mode');
        }

        if (type !== 'deposit') {
          excludeKeysEdit.push('kind');
          excludeKeysView.push('kind');
        }

        const isParentWallet = wallet.parent_id === null;
        if (!isParentWallet) {
          excludeKeysEdit.push(
            'wallet_address',
            'login_credentials_json',
            'provider',
            'bank_code',
            'wallet_holder_name',
            'bank_account_number',
            'kind',
          );
        } else {
          excludeKeysView.push('parent_id', 'merchant_short_name');
        }

        // For Deposit wallets, do not show credentials field for parent wallets
        if (isParentWallet && type === 'deposit') {
          excludeKeysEdit.push('login_credentials_json');
        }

        const handleEditWallet = (formData: FormData) => {
          const credentials = formData.get('login_credentials_json');

          if (!credentials) {
            formData.delete('login_credentials_json');
          }

          if (!formData.get('kind')) {
            formData.delete('kind');
          }

          return formData;
        };

        let additionalKeys: Partial<Record<keyof IWallet, string>>[] = [
          { login_credentials_json: '', provider: wallet.provider.id },
        ];

        // For Deposit parent wallets, do not include credentials field in edit form
        if (type === 'deposit' && isParentWallet) {
          additionalKeys = [{ provider: wallet.provider.id, kind: wallet.kind || '' }];
        }

        const actionBtnsConfig: ITableActionsConfig = {
          clone: {
            isEnabled: isParentWallet,
            isVisible: isParentWallet,
            popupElem: <CloneWallet wallet={wallet} refetch={refetch} />,
            popupSize: 'md',
            allowedRoles: ['admin', 'operator'],
          },
          add: {
            isEnabled: isParentWallet,
            isVisible: true,
            popupElem: (
              <NewWalletMID
                parent_id={wallet.id}
                parent_name={wallet.name}
                refetch={refetch}
                shouldSubmitDefaultSettings={false}
              />
            ),
            popupSize: 'md',
            allowedRoles: ['admin', 'operator'],
          },
          edit: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalEditEntity
                title={`Edit Wallet ${wallet.name}`}
                entity={wallet}
                excludeKeys={excludeKeysEdit}
                readOnlyKeys={isParentWallet ? { wallet_address: { tooltip: 'UPI ID cannot be changed after wallet creation', hasCopyBtn: true } } : undefined}
                additionalKeys={additionalKeys}
                useEditEntityMutation={useUpdateWalletMutation}
                onTransformFormData={handleEditWallet}
                refetch={refetch}
              />
            ),
            popupSize: 'md',
            allowedRoles: ['admin', 'merchant', 'operator'],
          },
          'edit-settings': {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <EditWalletSettings
                parent_id={wallet.id}
                parent_name={wallet.name}
                refetch={refetch}
              />
            ),
            popupSize: 'md',
          },
          view: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalViewEntity
                entity={wallet}
                title={`View Wallet ${wallet.name}`}
                deepEntityValues={{ bank: ['name'], provider: ['name'] }}
                excludeKeys={excludeKeysView}
                additionalData={
                  type !== 'deposit' && [
                    {
                      label: 'Credentials:',
                      element: <WalletCredentials walletId={wallet.id} />,
                    },
                  ]
                }
              />
            ),
            popupSize: 'md',
          },
        };

        return <TableActionBtns config={actionBtnsConfig} />;
      }

      default:
        return wallet[key];
    }
  };

  return {
    columnTitles,
    renderFilterElement,
    renderRow,
    renderItem,
  };
};
